import {
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { Observable, forkJoin } from 'rxjs';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  YdsButtonComponent,
  YdsButtonBackComponent,
  YdsCardEditableComponent,
  YdsCheckboxComponent,
  YdsComboboxComponent,
  YdsDatePickerComponent,
  YdsDropdownComponent,
  YdsSpinnerComponent,
  YdsSwitchComponent,
  YdsTextAreaComponent,
  YdsTextFieldComponent,
  type YdsDropdownOption,
} from '../../../yds-angular';
import {
  defaultEndDate,
  defaultStartDate,
  formatDisplayDate,
  isFirstOfMonth,
  isInvalid,
  isLastOfMonth,
  startBeforeEndValidator,
  toFormDate,
} from '../../../shared/general-ledger-yds.utils';
import {
  GeneralLedgerAccountService,
  type GeneralLedgerAccountDetail,
  type GeneralLedgerAccountListItem,
  type GeneralLedgerOption,
} from '../generalledgeraccount.service';
import { GeneralledgerGroupService } from '../../generalledgergroup/generalledgergroup.service';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';
import { deleteGeneralLedgerAccountWithChecks } from '../generalledgeraccount-shared';
import {
  GeneralLedgerDisplayCriterionService,
  type BookingDescriptionItem,
  type DisplayCriterion,
} from '../generalledgerdisplaycriterion.service';
import {
  GeneralLedgerAccountVariableElementService,
  type VariableElement,
} from '../generalledgeraccountvariableelement.service';

/** Working-copy row for the "Variabel rekeningnummer" grid (edit mode). */
interface VariableElementRow {
  /** Id of the persisted record, or '' for a new row. */
  variableElementId: string;
  elementId: string;
  startPosition: number | null;
  numberOfPositions: number | null;
  fixedValue: string;
}

const MAX_END_DATE_ISO = '9999-12-31';
const KIND_OF_ACCOUNT_DEBIT = 'DEBIT';
const KIND_OF_ACCOUNT_CREDIT = 'CREDIT';

/**
 * Largest lengths the database accepts (EF config in the backend's yfokbjContext.cs):
 * "Nummer" (GeneralLedger_Account) is nvarchar(25) and "Omschrijving"
 * (GeneralLedgerAccount.Name) is nvarchar(50). Values over these are rejected on
 * save, so guard against them up front instead of letting the save fail.
 */
const MAX_NUMBER_LENGTH = 25;
const MAX_NAME_LENGTH = 50;

@Component({
  selector: 'app-generalledgeraccount-editable',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    YdsCardEditableComponent,
    YdsButtonComponent,
    YdsButtonBackComponent,
    YdsCheckboxComponent,
    YdsSpinnerComponent,
    YdsSwitchComponent,
    YdsDropdownComponent,
    YdsComboboxComponent,
    YdsDatePickerComponent,
    YdsTextAreaComponent,
    YdsTextFieldComponent,
  ],
  templateUrl: './generalledgeraccount-editable.component.html',
  styleUrl: './generalledgeraccount-editable.component.scss',
})
export class GeneralledgeraccountEditableComponent {
  readonly isInvalid = isInvalid;
  readonly translate = inject(TranslateService);

  /** Only the first day of a month can be selected as start date. */
  readonly startDateFilter = (date: Date): boolean => isFirstOfMonth(date);

  /** Only the last day of a month can be selected as end date. */
  readonly endDateFilter = (date: Date): boolean => isLastOfMonth(date);

  private formBuilder = inject(FormBuilder);
  private accountService = inject(GeneralLedgerAccountService);
  private displayCriterionService = inject(GeneralLedgerDisplayCriterionService);
  private variableElementService = inject(GeneralLedgerAccountVariableElementService);
  private groupService = inject(GeneralledgerGroupService);
  private dialogService = inject(ConfirmDialogService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private document = inject(DOCUMENT);

  private readonly detailsCard = viewChild('detailsCard', { read: ElementRef<HTMLElement> });

  /** Id of the account being edited, or null in add mode. */
  readonly accountId = signal<string | null>(null);

  /**
   * Parent general ledger id. Provided as a route parameter in add mode (it cannot be
   * selected manually) and taken from the loaded record otherwise.
   */
  readonly generalLedgerId = signal<string>('');

  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly notFound = signal(false);

  /** The last loaded/saved record, used for the read-only view and to revert on cancel. */
  private readonly record = signal<GeneralLedgerAccountDetail | null>(null);

  readonly detailsEditing = signal(false);

  /** "Grootboekrekeningengroep" dropdown options. */
  readonly generalLedgerGroupOptions = signal<YdsDropdownOption[]>([]);

  /** General ledgers, used to resolve the parent ledger name (e.g. in add mode). */
  readonly generalLedgers = signal<GeneralLedgerOption[]>([]);

  /** Parent general ledger name for view-only display. */
  readonly generalLedgerName = computed(() => {
    const fromRecord = this.record()?.generalLedgerName;
    if (fromRecord) {
      return fromRecord;
    }
    const id = this.generalLedgerId();
    return this.generalLedgers().find((g) => g.id === id)?.name ?? '';
  });

  /** Fixed "Soort rekening" options (DEBIT = Debet, CREDIT = Credit). */
  readonly kindOfAccountOptions: readonly YdsDropdownOption[] = [
    { id: KIND_OF_ACCOUNT_DEBIT, label: 'Debet' },
    { id: KIND_OF_ACCOUNT_CREDIT, label: 'Credit' },
  ];

  readonly form: FormGroup = this.formBuilder.group(
    {
      generalLedgerAccount: ['', Validators.required],
      generalLedgerAccountName: ['', Validators.required],
      generalLedgerGroupId: [null],
      kindOfAccount: [KIND_OF_ACCOUNT_DEBIT, Validators.required],
      bookNegativeValues: [false],
      bookByEmployment: [false],
      startDate: [defaultStartDate(), Validators.required],
      endDate: [null],
      memoValue: [''],
    },
    { validators: startBeforeEndValidator() },
  );

  /** Whether we are creating a new account (no id in the route). */
  readonly isAddMode = computed(() => this.accountId() === null);

  /** Display text for the "Grootboekrekeningengroep" value in view mode. */
  readonly generalLedgerGroupName = computed(() => {
    const current = this.record();
    if (current?.generalLedgerGroupName) {
      return current.generalLedgerGroupName;
    }
    const selectedId = this.form.get('generalLedgerGroupId')?.value as string | null;
    return this.generalLedgerGroupOptions().find((o) => o.id === selectedId)?.label ?? '';
  });

  /** "Grootboekrekeningengroep" label for view mode, falling back to "n.v.t.". */
  readonly generalLedgerGroupDisplay = computed(() => this.generalLedgerGroupName() || 'n.v.t.');

  /** Account number for view mode. */
  readonly accountNumber = computed(() => this.record()?.generalLedgerAccount ?? '');

  /** Account name for view mode. */
  readonly accountName = computed(() => this.record()?.generalLedgerAccountName ?? '');

  /** "Soort rekening" label for view mode. */
  readonly kindOfAccountDisplay = computed(() =>
    this.record()?.kindOfAccount === KIND_OF_ACCOUNT_CREDIT ? 'Credit' : 'Debet',
  );

  readonly bookNegativeValuesOn = computed(() =>
    Boolean(this.record()?.bookNegativeValuesIndicator),
  );

  readonly bookByEmploymentOn = computed(() => Boolean(this.record()?.bookByEmployment));

  readonly startDateDisplay = computed(() => formatDisplayDate(this.record()?.startDate, 'nl-NL'));

  readonly endDateDisplay = computed(() => {
    const end = this.record()?.endDate;
    return this.isMaxEndDate(end) ? 'n.v.t.' : formatDisplayDate(end, 'nl-NL');
  });

  readonly memoDisplay = computed(() => this.record()?.memoValue || 'n.v.t.');

  // --- "Elementen boekingsomschrijving" card state ------------------------------

  readonly displayCriteriaEditing = signal(false);
  readonly isSavingDisplayCriteria = signal(false);

  /** All selectable booking descriptions, per language (unordered as received). */
  private readonly bookingDescriptionsNL = signal<BookingDescriptionItem[]>([]);
  private readonly bookingDescriptionsEN = signal<BookingDescriptionItem[]>([]);

  /** Criteria persisted for this account (source of truth, holds ids incl. faked ones). */
  private readonly displayCriteria = signal<DisplayCriterion[]>([]);

  /** Working copy of the checked numbers while editing. */
  readonly editedCriteriaNumbers = signal<Set<number>>(new Set<number>());

  /**
   * Selectable options as checkboxes, in the current language and ordered:
   * criteria 1-8 first, then Persoonscode (-1) and Persoonsnaam (-2) at the end.
   */
  readonly criteriaOptions = computed<BookingDescriptionItem[]>(() => {
    const list = this.useEnglish() ? this.bookingDescriptionsEN() : this.bookingDescriptionsNL();
    return [...list].sort((a, b) => this.criterionOrderKey(a.number) - this.criterionOrderKey(b.number));
  });

  /** Descriptions of the currently selected criteria, ordered, for the view mode. */
  readonly selectedCriteriaLabels = computed<string[]>(() => {
    const selected = new Set(this.displayCriteria().map((c) => c.number));
    return this.criteriaOptions()
      .filter((option) => selected.has(option.number))
      .map((option) => option.description);
  });

  private useEnglish(): boolean {
    return (this.translate.getCurrentLang() ?? '').toLowerCase().startsWith('en');
  }

  /** Sort key placing 1-8 first (ascending), then -1 and -2 last (in that order). */
  private criterionOrderKey(n: number): number {
    return n >= 0 ? n : 1000 - n;
  }

  // --- "Variabel rekeningnummer" card state -------------------------------------

  /** Each row contributes at least one character to the max-25 account number. */
  readonly maxVariableRows = 25;
  /** "Vaste waarde" is capped at 25 characters. */
  readonly fixedValueMaxLength = 25;

  readonly variableEditing = signal(false);
  readonly isSavingVariable = signal(false);

  /** Selectable "Rubriek" options. */
  readonly elementOptions = signal<YdsDropdownOption[]>([]);

  /** Persisted variable elements for this account (view mode + revert source). */
  readonly variableElements = signal<VariableElement[]>([]);

  /** Working copy of the rows shown in edit mode. */
  readonly variableRows = signal<VariableElementRow[]>([]);

  /** Ids of persisted rows removed during edit, deleted on save. */
  private removedVariableElementIds: string[] = [];

  /** Whether another "Variabel rekeningnummer" row may still be added. */
  readonly canAddVariableRow = computed(() => this.variableRows().length < this.maxVariableRows);

  constructor() {
    const id = this.route.snapshot.paramMap.get('id');
    this.loadGeneralLedgerGroups();
    this.loadGeneralLedgers();

    if (id) {
      this.accountId.set(id);
      this.loadAccount(id);
      this.loadBookingDescriptions();
      this.loadDisplayCriteria(id);
      this.loadElementOptions();
      this.loadVariableElements(id);
    } else {
      // Add mode: start directly in edit state and capture the parent ledger from the route.
      this.detailsEditing.set(true);
      const ledgerId =
        this.route.snapshot.paramMap.get('generalLedgerId') ??
        this.route.snapshot.queryParamMap.get('generalLedgerId') ??
        '';
      this.generalLedgerId.set(ledgerId);
    }
  }

  private loadGeneralLedgerGroups(): void {
    this.groupService
      .getByContext()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((groups) => {
        this.generalLedgerGroupOptions.set(groups.map((g) => ({ id: g.id, label: g.name })));
      });
  }

  private loadGeneralLedgers(): void {
    this.accountService
      .getGeneralLedgersByContext()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ledgers) => {
        this.generalLedgers.set(ledgers);
      });
  }

  private loadAccount(id: string): void {
    this.isLoading.set(true);
    this.accountService
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (account) => {
          if (!account) {
            this.notFound.set(true);
          } else {
            this.record.set(account);
            this.generalLedgerId.set(account.generalLedgerId);
            this.patchForm(account);
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.notFound.set(true);
          this.isLoading.set(false);
        },
      });
  }

  private patchForm(account: GeneralLedgerAccountDetail): void {
    this.form.patchValue({
      generalLedgerAccount: account.generalLedgerAccount,
      generalLedgerAccountName: account.generalLedgerAccountName,
      generalLedgerGroupId: account.generalLedgerGroupId,
      kindOfAccount: account.kindOfAccount,
      bookNegativeValues: account.bookNegativeValuesIndicator,
      bookByEmployment: account.bookByEmployment,
      startDate: toFormDate(account.startDate),
      endDate: this.isMaxEndDate(account.endDate) ? null : toFormDate(account.endDate),
      memoValue: account.memoValue ?? '',
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private isMaxEndDate(value: unknown): boolean {
    const date = toFormDate(value as Date | string | null);
    if (!date) {
      return false;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}` === MAX_END_DATE_ISO;
  }

  // --- Details card actions -----------------------------------------------------

  onDetailsCancel(): void {
    if (this.isAddMode()) {
      this.goBack();
      return;
    }

    const current = this.record();
    if (current) {
      this.patchForm(current);
    }
    this.detailsEditing.set(false);
    this.scrollToDetails();
  }

  onDetailsNext(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value as {
      generalLedgerAccount: string;
      generalLedgerAccountName: string;
      generalLedgerGroupId: string | null;
      kindOfAccount: string;
      bookNegativeValues: boolean;
      bookByEmployment: boolean;
      startDate: Date | null;
      endDate: Date | null;
      memoValue: string;
    };

    // Length guards: "Nummer" and "Omschrijving" have fixed column sizes, so an
    // over-long value is rejected on save. Explain that up front rather than
    // letting it fail (in both add and "Wijzigen" mode).
    if ((value.generalLedgerAccount?.length ?? 0) > MAX_NUMBER_LENGTH) {
      this.dialogService.open({
        width: '320px',
        title: 'Tekst te lang',
        message: `Het nummer mag maximaal ${MAX_NUMBER_LENGTH} tekens bevatten.`,
        confirmText: 'Ok',
        variant: 'warning',
      });
      return;
    }

    if ((value.generalLedgerAccountName?.length ?? 0) > MAX_NAME_LENGTH) {
      this.dialogService.open({
        width: '320px',
        title: 'Tekst te lang',
        message: `De omschrijving mag maximaal ${MAX_NAME_LENGTH} tekens bevatten.`,
        confirmText: 'Ok',
        variant: 'warning',
      });
      return;
    }

    const selectedGroupId = value.generalLedgerGroupId || null;
    const selectedGroupName = selectedGroupId
      ? (this.generalLedgerGroupOptions().find((o) => o.id === selectedGroupId)?.label ?? '')
      : '';

    const payload: GeneralLedgerAccountDetail = {
      generalLedgerAccountId: this.accountId() ?? '',
      generalLedgerAccount: value.generalLedgerAccount,
      generalLedgerAccountName: value.generalLedgerAccountName,
      generalLedgerId: this.generalLedgerId(),
      generalLedgerName: this.record()?.generalLedgerName ?? '',
      generalLedgerGroupId: selectedGroupId,
      generalLedgerGroupName: selectedGroupName,
      kindOfAccount: value.kindOfAccount,
      bookNegativeValuesIndicator: value.bookNegativeValues,
      bookByEmployment: value.bookByEmployment,
      startDate: (value.startDate ?? defaultStartDate()) as Date,
      endDate: (value.endDate ?? defaultEndDate()) as Date,
      memoValue: value.memoValue ?? '',
    };

    this.isSaving.set(true);

    // Guard against a duplicate "Nummer" + "Omschrijving": the backend rejects a
    // second account with the same combination within the same general ledger, but
    // that rejection is flattened to a generic error before it reaches us. Check up
    // front so the conflict can be explained instead of the save failing silently.
    this.accountService
      .getByContext(this.generalLedgerId())
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (accounts) => {
          if (
            this.isDuplicateAccount(
              accounts,
              value.generalLedgerAccount,
              value.generalLedgerAccountName,
            )
          ) {
            this.isSaving.set(false);
            this.dialogService.open({
              width: '320px',
              title: 'Nummer en omschrijving combinatie bestaat al',
              message: `Er bestaat al een grootboekrekening met nummer en omschrijving '${value.generalLedgerAccount}', '${value.generalLedgerAccountName}'. Kies een ander nummer of een andere omschrijving.`,
              confirmText: 'Ok',
              variant: 'warning',
            });
            return;
          }
          this.saveAccount(payload);
        },
        // If the duplicate check itself fails, still attempt the save; the backend
        // remains the source of truth and any error is surfaced by saveAccount().
        error: () => this.saveAccount(payload),
      });
  }

  /**
   * True when another account already uses this "Grootboek" + "Nummer" +
   * "Omschrijving" combination.
   */
  private isDuplicateAccount(
    accounts: GeneralLedgerAccountListItem[],
    accountNumber: string,
    accountName: string,
  ): boolean {
    const targetNumber = (accountNumber ?? '').trim().toLowerCase();
    const targetName = (accountName ?? '').trim().toLowerCase();
    const targetLedgerId = (this.generalLedgerId() ?? '').trim().toLowerCase();
    const currentId = this.accountId();
    return accounts.some(
      (account) =>
        account.generalLedgerAccountId !== currentId &&
        (account.generalLedgerId ?? '').trim().toLowerCase() === targetLedgerId &&
        (account.generalLedger_Account ?? '').trim().toLowerCase() === targetNumber &&
        (account.generalLedger_Account_Name ?? '').trim().toLowerCase() === targetName,
    );
  }

  private saveAccount(payload: GeneralLedgerAccountDetail): void {
    const id = this.accountId();
    const save$ = id
      ? this.accountService.update(id, payload)
      : this.accountService.add(payload);

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (saved) => {
        this.isSaving.set(false);
        if (id) {
          this.record.set({ ...payload, ...saved });
          this.detailsEditing.set(false);
          this.scrollToDetails();
        } else if (saved?.generalLedgerAccountId) {
          this.router.navigate([
            '/config/generalledgeraccount/editable',
            saved.generalLedgerAccountId,
          ]);
        } else {
          this.goBack();
        }
      },
      // Length and duplicate "Nummer" are already checked up front, so this only
      // fires for other, unexpected failures. The backend error is flattened
      // upstream, so keep the message neutral rather than guessing a cause.
      error: () => {
        this.isSaving.set(false);
        this.dialogService.open({
          width: '320px',
          title: 'Opslaan niet gelukt',
          message:
            'Het opslaan is niet gelukt. Controleer de ingevoerde gegevens en probeer het opnieuw.',
          confirmText: 'Ok',
          variant: 'warning',
        });
      },
    });
  }

  // --- "Elementen boekingsomschrijving" card actions ----------------------------

  private loadBookingDescriptions(): void {
    this.displayCriterionService
      .getBookingDescriptionList()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lists) => {
        this.bookingDescriptionsNL.set(lists.nl);
        this.bookingDescriptionsEN.set(lists.en);
      });
  }

  private loadDisplayCriteria(accountId: string): void {
    this.displayCriterionService
      .getByAccount(accountId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((criteria) => {
        this.displayCriteria.set(criteria);
        this.resetEditedCriteria();
      });
  }

  /** Resets the working copy of checked numbers to the persisted selection. */
  private resetEditedCriteria(): void {
    this.editedCriteriaNumbers.set(new Set(this.displayCriteria().map((c) => c.number)));
  }

  isCriterionChecked(number: number): boolean {
    return this.editedCriteriaNumbers().has(number);
  }

  toggleCriterion(number: number, checked: boolean): void {
    this.editedCriteriaNumbers.update((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(number);
      } else {
        next.delete(number);
      }
      return next;
    });
  }

  onDisplayCriteriaCancel(): void {
    this.resetEditedCriteria();
    this.displayCriteriaEditing.set(false);
  }

  onDisplayCriteriaSave(): void {
    const id = this.accountId();
    if (!id) {
      return;
    }

    const original = this.displayCriteria();
    const originalNumbers = new Set(original.map((c) => c.number));
    const edited = this.editedCriteriaNumbers();

    // Diff the working copy against the persisted selection: newly checked numbers
    // are added, unchecked existing ones are deleted (by id). Persoonscode (-1) and
    // Persoonsnaam (-2) are handled the same way; the backend keeps their table in sync.
    const additions = [...edited].filter((number) => !originalNumbers.has(number));
    const removals = original.filter((criterion) => !edited.has(criterion.number));

    if (additions.length === 0 && removals.length === 0) {
      this.displayCriteriaEditing.set(false);
      return;
    }

    this.isSavingDisplayCriteria.set(true);

    const operations: Observable<unknown>[] = [
      ...removals.map((criterion) =>
        this.displayCriterionService.remove(criterion.generalLedgerDisplayCriterionId),
      ),
      ...additions.map((number) => this.displayCriterionService.add(id, number)),
    ];

    forkJoin(operations)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSavingDisplayCriteria.set(false);
          this.displayCriteriaEditing.set(false);
          this.loadDisplayCriteria(id);
        },
        // Reload so the checkboxes reflect the true server state (a partial save may
        // have applied some changes) and tell the user it failed.
        error: () => {
          this.isSavingDisplayCriteria.set(false);
          this.loadDisplayCriteria(id);
          this.dialogService.open({
            width: '320px',
            title: 'Opslaan niet gelukt',
            message:
              'Het opslaan van de boekingsomschrijving is niet gelukt. Probeer het opnieuw.',
            confirmText: 'Ok',
            variant: 'warning',
          });
        },
      });
  }

  // --- "Variabel rekeningnummer" card actions -----------------------------------

  private loadElementOptions(): void {
    this.variableElementService
      .getElementOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => this.elementOptions.set(options));
  }

  private loadVariableElements(accountId: string): void {
    this.variableElementService
      .getByAccount(accountId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((elements) => {
        this.variableElements.set(elements);
        this.resetVariableRows();
      });
  }

  /** Resets the working copy to the persisted rows and clears pending deletes. */
  private resetVariableRows(): void {
    this.removedVariableElementIds = [];
    this.variableRows.set(
      this.variableElements().map((element) => ({
        variableElementId: element.variableElementId,
        elementId: element.elementId,
        startPosition: element.startPosition,
        numberOfPositions: element.numberOfPositions,
        fixedValue: element.fixedValue,
      })),
    );
  }

  /** Label for a persisted row's element (view mode). */
  variableElementLabel(elementId: string): string {
    return this.elementOptions().find((option) => option.id === elementId)?.label ?? elementId;
  }

  addVariableRow(): void {
    if (!this.canAddVariableRow()) {
      return;
    }
    // Beginpositie and Lengte default to 0, matching the backend DTO defaults.
    this.variableRows.update((rows) => [
      ...rows,
      {
        variableElementId: '',
        elementId: '',
        startPosition: 0,
        numberOfPositions: 0,
        fixedValue: '',
      },
    ]);
  }

  removeVariableRow(index: number): void {
    const rows = this.variableRows();
    const row = rows[index];
    if (row?.variableElementId) {
      this.removedVariableElementIds.push(row.variableElementId);
    }
    this.variableRows.set(rows.filter((_, i) => i !== index));
  }

  updateVariableRow(index: number, patch: Partial<VariableElementRow>): void {
    this.variableRows.update((rows) =>
      rows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  }

  onStartPositionChange(index: number, value: string): void {
    this.updateVariableRow(index, { startPosition: this.parseNullableInt(value) });
  }

  onNumberOfPositionsChange(index: number, value: string): void {
    this.updateVariableRow(index, { numberOfPositions: this.parseNullableInt(value) });
  }

  private parseNullableInt(value: string): number | null {
    const trimmed = (value ?? '').trim();
    if (trimmed === '') {
      return null;
    }
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
  }

  onVariableCancel(): void {
    this.resetVariableRows();
    this.variableEditing.set(false);
  }

  onVariableSave(): void {
    const id = this.accountId();
    if (!id) {
      return;
    }

    const operations: Observable<unknown>[] = [];

    // Rows removed during edit are deleted.
    this.removedVariableElementIds.forEach((removedId) =>
      operations.push(this.variableElementService.remove(removedId)),
    );

    // Existing rows are updated, new rows are added.
    this.variableRows().forEach((row) => {
      const model: VariableElement = {
        variableElementId: row.variableElementId,
        generalLedgerAccountId: id,
        elementId: row.elementId,
        startPosition: row.startPosition,
        numberOfPositions: row.numberOfPositions,
        fixedValue: row.fixedValue,
      };
      if (row.variableElementId) {
        operations.push(this.variableElementService.update(row.variableElementId, model));
      } else {
        operations.push(this.variableElementService.add(model));
      }
    });

    if (operations.length === 0) {
      this.variableEditing.set(false);
      return;
    }

    this.isSavingVariable.set(true);

    forkJoin(operations)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSavingVariable.set(false);
          this.variableEditing.set(false);
          this.loadVariableElements(id);
        },
        // Reload so the rows reflect the true server state (a partial save may have
        // applied some changes) and tell the user it failed.
        error: () => {
          this.isSavingVariable.set(false);
          this.loadVariableElements(id);
          this.dialogService.open({
            width: '320px',
            title: 'Opslaan niet gelukt',
            message:
              'Het opslaan van het variabel rekeningnummer is niet gelukt. Probeer het opnieuw.',
            confirmText: 'Ok',
            variant: 'warning',
          });
        },
      });
  }

  // --- Page actions -------------------------------------------------------------

  private scrollToDetails(): void {
    setTimeout(() => {
      const element = this.detailsCard()?.nativeElement;
      if (!element) {
        return;
      }

      const chrome = this.document.querySelector('.app-chrome') as HTMLElement | null;
      const offset = chrome?.offsetHeight ?? 0;
      const top = element.getBoundingClientRect().top + window.scrollY - offset - 16;

      window.scrollTo({ top, behavior: 'smooth' });
    });
  }

  goBack(): void {
    const ledgerId = this.generalLedgerId();
    if (ledgerId) {
      this.router.navigate(['/config/generalledgeraccount', ledgerId]);
    } else {
      this.router.navigateByUrl('config/generalledgeraccount');
    }
  }

  goToGeneralLedger(): void {
    const ledgerId = this.generalLedgerId();
    if (ledgerId) {
      this.router.navigate(['/config/generalledger/editable', ledgerId]);
    } else {
      this.router.navigate(['/config/generalledger']);
    }
  }

  goToGeneralLedgerGroup(): void {
    const groupId =
      (this.form.get('generalLedgerGroupId')?.value as string | null) ??
      this.record()?.generalLedgerGroupId ??
      null;
    if (groupId) {
      this.router.navigate(['/config/generalledgergroup/editable', groupId]);
    } else {
      this.router.navigate(['/config/generalledgergroup']);
    }
  }

  onDelete(): void {
    const id = this.accountId();
    if (!id) {
      return;
    }

    deleteGeneralLedgerAccountWithChecks({
      id,
      service: this.accountService,
      dialogService: this.dialogService,
      destroyRef: this.destroyRef,
      onDeleted: () => this.goBack(),
    });
  }
}
